import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const Home = () => {
  const { t } = useTranslation();
  const [visibleSections, setVisibleSections] = useState({});
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  
  // Fallback products (used if API fails) - Top 6
  const fallbackProducts = [
    { id: 1, name: t('products.items.laminarAirFlow.name'), slug: 'ductless-laminar-air-flow-system', shortDescription: t('products.items.laminarAirFlow.description'), category: t('products.categories.airQuality') },
    { id: 2, name: t('products.items.ozoneGenerator.name'), slug: 'ozone-generator', shortDescription: t('products.items.ozoneGenerator.description'), category: t('products.categories.disinfection') },
    { id: 3, name: t('products.items.oxygenPlant.name'), slug: 'psa-oxygen-generation-plant', shortDescription: t('products.items.oxygenPlant.description'), category: t('products.categories.oxygen') },
    { id: 4, name: t('products.items.modularOT.name'), slug: 'modular-operation-theatre', shortDescription: t('products.items.modularOT.description'), category: t('products.categories.operationTheatre') },
    { id: 5, name: 'Isolation Ward', slug: 'isolation-ward', shortDescription: 'Negative pressure isolation rooms for infectious disease control.', category: t('products.categories.isolation') },
    { id: 6, name: 'Air Handling Unit', slug: 'ductless-air-handling-unit', shortDescription: 'Advanced air handling system for operation theatres.', category: t('products.categories.airHandling') },
  ];

  // Fetch products from Notion API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get('/api/products');
        if (response.data.success && response.data.data.length > 0) {
          // Get top 6 products (featured first, then by order)
          const allProducts = response.data.data;
          const featuredProducts = allProducts.filter(p => p.featured);
          const nonFeaturedProducts = allProducts.filter(p => !p.featured);
          // Combine featured first, then others, limit to 6
          const displayProducts = [...featuredProducts, ...nonFeaturedProducts].slice(0, 6);
          setProducts(displayProducts);
        } else {
          setProducts(fallbackProducts);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
        setProducts(fallbackProducts);
      } finally {
        setProductsLoading(false);
      }
    };
    fetchProducts();
  }, []);
  
  // Intersection Observer for continuous scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Set to true when entering viewport, false when leaving
          // This allows animations to re-trigger on every scroll
          setVisibleSections((prev) => ({
            ...prev,
            [entry.target.id]: entry.isIntersecting,
          }));
        });
      },
      { threshold: 0.15, rootMargin: '-80px' }
    );

    const sections = document.querySelectorAll('.animate-section');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const whyChooseUs = [
    { id: 1, title: t('whyChoose.quality.title'), description: t('whyChoose.quality.description'), icon: '✓' },
    { id: 2, title: t('whyChoose.patented.title'), description: t('whyChoose.patented.description'), icon: '📜' },
    { id: 3, title: t('whyChoose.service.title'), description: t('whyChoose.service.description'), icon: '🔧' },
    { id: 4, title: t('whyChoose.costEfficient.title'), description: t('whyChoose.costEfficient.description'), icon: '💰' },
    { id: 5, title: t('whyChoose.excellence.title'), description: t('whyChoose.excellence.description'), icon: '🎯' },
  ];

  const testimonials = [
    { id: 1, name: 'Dr. Akshay Nadkarni', hospital: '21st Century Hospital, VAPI', quote: 'You can rely on Al-Med Equipments - these guys are fast, efficient and their work is unbelievable' }
  ];

  const icons = ['🏥', '💨', '🔬', '⚗️', '🛡️', '🌬️'];

  return (
    <div className="home">
      {/* Hero Section - Straumann Cinematic Style */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-mesh"></div>
          <div className="hero-grid"></div>
          <div className="hero-orb hero-orb-1"></div>
          <div className="hero-orb hero-orb-2"></div>
          <div className="hero-orb hero-orb-3"></div>
        </div>
        
        <div className="hero-content">
          <div className="hero-badge">
            <span className="hero-badge-dot"></span>
            {t('hero.badge')}
          </div>
          
          <h1 className="hero-title">
            {t('hero.title1')}
            <span>{t('hero.title2')}</span>
          </h1>
          
          <p className="hero-description">
            {t('hero.description')}
          </p>
          
          <div className="hero-buttons">
            <Link to="/products" className="btn btn-primary">{t('hero.exploreProducts')}</Link>
            <Link to="/contact" className="btn btn-ghost">{t('hero.requestQuote')}</Link>
          </div>
          
          <div className="hero-stats">
            <div className="hero-stat">
              <div className="hero-stat-number">15+</div>
              <div className="hero-stat-label">{t('hero.stats.years')}</div>
            </div>
            <div className="hero-stat">
              <div className="hero-stat-number">200+</div>
              <div className="hero-stat-label">{t('hero.stats.clients')}</div>
            </div>
            <div className="hero-stat">
              <div className="hero-stat-number">500+</div>
              <div className="hero-stat-label">{t('hero.stats.projects')}</div>
            </div>
            <div className="hero-stat">
              <div className="hero-stat-number">24/7</div>
              <div className="hero-stat-label">{t('hero.stats.support')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Discover Section - with scroll animation */}
      <section 
        id="discover" 
        className="section discover-section animate-section"
      >
        <div className="container">
          <div 
            className="section-header"
            style={{
              opacity: visibleSections.discover ? 1 : 0,
              transform: visibleSections.discover ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <span className="section-badge">{t('discover.badge')}</span>
            <h2 className="section-title">{t('discover.title')}</h2>
            <p className="section-description">
              {t('discover.description')}
            </p>
          </div>

          <div className="discover-grid">
            {[
              { icon: '🏥', title: t('discover.productsServices.title'), desc: t('discover.productsServices.description'), link: '/products' },
              { icon: '🏆', title: t('discover.aboutUs.title'), desc: t('discover.aboutUs.description'), link: '/about' },
              { icon: '📸', title: t('discover.gallery.title'), desc: t('discover.gallery.description'), link: '/gallery' },
              { icon: '📞', title: t('discover.contactSupport.title'), desc: t('discover.contactSupport.description'), link: '/contact' },
            ].map((item, index) => (
              <Link 
                to={item.link} 
                className="discover-card"
                key={index}
                style={{
                  opacity: visibleSections.discover ? 1 : 0,
                  transform: visibleSections.discover ? 'translateY(0)' : 'translateY(60px)',
                  transition: `all 0.8s cubic-bezier(0.4, 0, 0.2, 1) ${0.1 + index * 0.1}s`
                }}
              >
                <div className="discover-card-icon">{item.icon}</div>
                <h3 className="discover-card-title">{item.title}</h3>
                <p className="discover-card-description">{item.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section 
        id="products" 
        className="section animate-section"
      >
        <div className="container">
          <div 
            className="section-header"
            style={{
              opacity: visibleSections.products ? 1 : 0,
              transform: visibleSections.products ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <span className="section-badge">{t('products.badge')}</span>
            <h2 className="section-title">{t('products.title')}</h2>
            <p className="section-description">
              {t('products.description')}
            </p>
          </div>

          <div className="products-grid">
            {productsLoading ? (
              <div className="loading" style={{ gridColumn: '1 / -1', padding: '2rem' }}>
                <div className="spinner"></div>
              </div>
            ) : (
              products.map((product, index) => (
                <Link 
                  to={`/products/${product.slug}`} 
                  key={product.id} 
                  className="product-card"
                  style={{
                    opacity: visibleSections.products ? 1 : 0,
                    transform: visibleSections.products ? 'translateY(0)' : 'translateY(60px)',
                    transition: `all 0.8s cubic-bezier(0.4, 0, 0.2, 1) ${0.1 + index * 0.15}s`
                  }}
                >
                  <div className="product-card-image">
                    {product.image ? (
                      <img src={product.image} alt={product.name} />
                    ) : (
                      <span className="product-icon">{icons[index % icons.length]}</span>
                    )}
                  </div>
                  <div className="product-card-content">
                    <span className="product-card-category">{product.category}</span>
                    <h3 className="product-card-title">{product.name}</h3>
                    <p className="product-card-description">{product.shortDescription}</p>
                    <span className="btn btn-secondary">{t('products.learnMore')}</span>
                  </div>
                </Link>
              ))
            )}
          </div>

          <div 
            className="text-center mt-4"
            style={{
              opacity: visibleSections.products ? 1 : 0,
              transform: visibleSections.products ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.6s'
            }}
          >
            <Link to="/products" className="btn btn-primary">{t('products.viewAll')}</Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section 
        id="whychoose" 
        className="section why-choose-section animate-section"
      >
        <div className="container">
          <div 
            className="section-header"
            style={{
              opacity: visibleSections.whychoose ? 1 : 0,
              transform: visibleSections.whychoose ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <span className="section-badge">{t('whyChoose.badge')}</span>
            <h2 className="section-title">{t('whyChoose.title')}</h2>
            <p className="section-description">
              {t('whyChoose.description')}
            </p>
          </div>

          <div className="why-choose-grid">
            {whyChooseUs.map((item, index) => (
              <div 
                key={item.id} 
                className="why-choose-card"
                style={{
                  opacity: visibleSections.whychoose ? 1 : 0,
                  transform: visibleSections.whychoose ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.95)',
                  transition: `all 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${0.1 + index * 0.1}s`
                }}
              >
                <div className="why-choose-icon">{item.icon}</div>
                <h3 className="why-choose-title">{item.title}</h3>
                <p className="why-choose-description">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section 
        id="testimonials" 
        className="section testimonials-section animate-section"
      >
        <div className="container">
          <div 
            className="section-header"
            style={{
              opacity: visibleSections.testimonials ? 1 : 0,
              transform: visibleSections.testimonials ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <span className="section-badge">{t('testimonials.badge')}</span>
            <h2 className="section-title">{t('testimonials.title')}</h2>
          </div>

          {testimonials.map((testimonial) => (
            <div 
              key={testimonial.id} 
              className="testimonial-card"
              style={{
                opacity: visibleSections.testimonials ? 1 : 0,
                transform: visibleSections.testimonials ? 'translateY(0) scale(1)' : 'translateY(60px) scale(0.95)',
                transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.2s'
              }}
            >
              <p className="testimonial-quote">"{testimonial.quote}"</p>
              <p className="testimonial-author">{testimonial.name}</p>
              <p className="testimonial-position">{testimonial.hospital}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <h2 className="cta-title">{t('cta.title')}</h2>
          <p className="cta-description">
            {t('cta.description')}
          </p>
          <div className="hero-buttons">
            <Link to="/contact" className="btn btn-white">{t('cta.requestQuote')}</Link>
            <a href="tel:+919322510636" className="btn btn-ghost">
              {t('cta.callNow')}: +91 93225 10636
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
